import { useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShieldCheck } from 'lucide-react'

import { ApiError } from '@/api/client'
import { authApi } from '@/api/endpoints'
import { useAuth } from '@/auth/AuthContext'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { TextField } from '@/components/ui/Field'
import { PasswordRules } from '@/components/ui/PasswordRules'
import { isPasswordValid } from '@/lib/password'

/**
 * Zmiana hasła. Backend po zmianie unieważnia wszystkie sesje, w tym bieżącą,
 * więc użytkownik zawsze wraca na ekran logowania.
 */
export function ChangePasswordPage() {
  const { user, mustChangePassword, logout, clearSession } = useAuth()
  const navigate = useNavigate()

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const mismatch = confirmation.length > 0 && confirmation !== newPassword
  const canSubmit =
    currentPassword.length > 0 &&
    isPasswordValid(newPassword) &&
    !mismatch &&
    confirmation.length > 0

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setIsSubmitting(true)

    try {
      await authApi.changePassword(currentPassword, newPassword)
      // Serwer unieważnił wszystkie sesje, w tym bieżącą — czyścimy stan lokalny,
      // inaczej strażnik trasy uznałby użytkownika za wciąż zalogowanego.
      clearSession()
      navigate('/logowanie', {
        replace: true,
        state: { notice: 'Hasło zostało zmienione. Zaloguj się nowym hasłem.' },
      })
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Nie udało się zmienić hasła.')
      setIsSubmitting(false)
    }
  }

  const handleCancel = async () => {
    if (mustChangePassword) {
      await logout()
      navigate('/logowanie', { replace: true })
    } else {
      navigate(-1)
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="card p-8">
          <header className="mb-6 flex items-start gap-4">
            <span className="rounded-lg bg-primary/10 p-3 text-primary" aria-hidden="true">
              <ShieldCheck size={24} />
            </span>
            <div>
              <h1 className="font-headline text-2xl font-bold text-secondary">
                {mustChangePassword ? 'Ustaw nowe hasło' : 'Zmiana hasła'}
              </h1>
              <p className="mt-1 text-sm text-on-surface-variant">{user?.email}</p>
            </div>
          </header>

          {mustChangePassword && (
            <Alert tone="warning" title="Wymagana zmiana hasła" className="mb-6">
              Twoje konto korzysta z hasła nadanego przez administratora. Ustaw własne, aby przejść
              do systemu.
            </Alert>
          )}

          {error && (
            <Alert tone="error" className="mb-6">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <TextField
              label="Obecne hasło"
              type="password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              autoComplete="current-password"
              required
              autoFocus
            />

            <div className="space-y-3">
              <TextField
                label="Nowe hasło"
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                autoComplete="new-password"
                required
              />
              <PasswordRules password={newPassword} />
            </div>

            <TextField
              label="Powtórz nowe hasło"
              type="password"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              autoComplete="new-password"
              error={mismatch ? 'Hasła nie są identyczne.' : undefined}
              required
            />

            <Alert tone="info">
              Po zmianie hasła zostaniesz wylogowany ze wszystkich urządzeń.
            </Alert>

            <div className="flex flex-wrap gap-3">
              <Button type="submit" size="lg" isLoading={isSubmitting} disabled={!canSubmit}>
                Zmień hasło
              </Button>
              <Button type="button" variant="ghost" size="lg" onClick={handleCancel}>
                {mustChangePassword ? 'Wyloguj się' : 'Anuluj'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
